import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  ReactNode,
  useCallback,
} from "react";
import { env } from "@/config/env";
import type { User, AuthState } from "@/shared/types/auth.types";
import { SUBCONTRACTOR_TYPE } from "@/shared/types/auth.types";
import { useTenantContext } from "./TenantProvider";
import { FeatureToggleService } from "@/shared/services/featureToggle.service";
import {
  apiClient as defaultApiClient,
  createTenantApiClient,
} from "@/shared/api/client";
import { apiRoutes } from "@/shared/api/routes";
import type { AxiosInstance } from "axios";
import { setServerTimeContext } from "@/shared/utils/serverTimeContext";
import { setAgencyContext, clearAgencyContext } from "@/shared/api/agencyContextStore";
import {
  clearTtauthRedirectMark,
  fetchLoginMode,
  loadTtauthSessionScript,
  signInWithTtauth,
  type LoginMode,
  type TtauthLoginError,
} from "@/shared/services/ttauthLogin.service";

/**
 * Base permissions that are restricted for Normal, Subcontractor, and Funding Source users.
 * Based on existing app behavior shown in screenshots.
 */
const RESTRICTED_PERMISSIONS_BASE = [
  'configuration_create',           // Configuration Parameters - Create only (View/Update allowed)
  'allowed_value_set_view',         // Custom Values - View
  'allowed_value_set_create',       // Custom Values - Create
  'allowed_value_set_update',       // Custom Values - Update
  'allowed_value_set_delete',       // Custom Values - Delete
  'report_template_update',         // Report Templates - Update (View is allowed)
  'metaroute_custom_field_view',    // Schedule Custom Fields - View
  'metaroute_custom_field_create',  // Schedule Custom Fields - Create
  'metaroute_custom_field_update',  // Schedule Custom Fields - Update
  'metaroute_custom_field_delete',  // Schedule Custom Fields - Delete
  'batch_job_delete',               // Batch Jobs - Delete (View, Create, Update are allowed)
];

/**
 * Additional module permissions restricted for Normal, Subcontractor, and Funding Source users.
 * Center users (type 3) have ACCESS to these modules.
 */
const RESTRICTED_MODULE_PERMISSIONS = [
  'bulk_import',                    // Bulk Import module
  'edi',                            // EDI module
  'reporting',                      // Reporting module
];

/**
 * Combined restrictions for Normal (0), Subcontractor (1), and Funding Source (2) users.
 */
const RESTRICTED_PERMISSIONS_NORMAL_SUBCONTRACTOR_FUNDING = [
  ...RESTRICTED_PERMISSIONS_BASE,
  ...RESTRICTED_MODULE_PERMISSIONS,
];

/**
 * Restrictions for Center User (3) - same base restrictions but WITH access to modules.
 */
const RESTRICTED_PERMISSIONS_CENTER_USER = [
  ...RESTRICTED_PERMISSIONS_BASE,
  // Center users have ACCESS to Bulk import, EDI, and Reporting (not restricted)
];

interface AuthContextType extends AuthState {
  bootstrapSession: () => Promise<boolean>;
  /**
   * ECOPD-14897: Re-authenticates with fresh credentials (login + new password)
   * without navigating to /login. Calls POST /drt/api/login to produce a new
   * RoR session (with fresh k3 credentials), then bootstraps session state.
   * Used after a self-password-change so the user stays logged in.
   */
  relogin: (login: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  /**
   * ECOPD-18285: how users sign in on this host ('password' | 'ttauth'), from the agency's
   * K3 TTAuth flag via the BFF. Null until loaded or when loading failed.
   */
  loginMode: LoginMode | null;
  /** Why the sign-in options could not be loaded (shown on the login page). */
  loginConfigError: TtauthLoginError | null;
  /** Why the last TTAuth sign-in failed (shown on the login page), e.g. not provisioned. */
  ttauthError: TtauthLoginError | null;
  can: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  apiClient: AxiosInstance;
  permissions: string[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

interface SessionBootstrapResponse {
  user_id?: number;
  id?: number;
  email?: string;
  login?: string;
  user_name?: string;
  user_type?: string;
  userType?: string;
  roles?: string[];
  permissions?: string[];
  tenant_id?: string;
  tenantId?: string;
  /** The Ecolane agency this session is bound to, e.g. "dev-react".
   *  Conv-AI states it back on /query so a scope mismatch is detectable. */
  agency_id?: string;
  subcontractor?: number;
  company_ids?: number[];
  funding_source_ids?: number[];
  center_id?: number | null;
  default_run_pool_id?: number | null;
  access_unassigned_no_run_trips?: boolean;
  /** ECOPD-18285: 'password' | 'ttauth' - how the BFF session was established. */
  auth_mode?: 'password' | 'ttauth';
  server_now?: string;
  server_today?: string;
  server_timezone?: string;
}

/**
 * Filter out restricted permissions based on user type.
 * - Normal (0), Subcontractor (1), Funding Source (2): Same restrictions including module access
 * - Center User (3): Base restrictions but WITH access to Bulk import, EDI, and Reporting
 * - Ecolane (4): No restrictions (full admin access)
 */
const filterPermissionsByUserType = (permissions: string[], subcontractorType?: number): string[] => {
  const userType = subcontractorType ?? SUBCONTRACTOR_TYPE.NORMAL;
  
  // Ecolane users get all permissions (no restrictions)
  if (userType === SUBCONTRACTOR_TYPE.ECOLANE) {
    return permissions;
  }
  
  // Center User (3): Base restrictions but WITH access to modules
  if (userType === SUBCONTRACTOR_TYPE.CLIENT_POOL) {
    return permissions.filter(permission => !RESTRICTED_PERMISSIONS_CENTER_USER.includes(permission));
  }
  
  // Normal (0), Subcontractor (1), Funding Source (2): Same restrictions
  if (
    userType === SUBCONTRACTOR_TYPE.NORMAL ||
    userType === SUBCONTRACTOR_TYPE.COMPANY ||
    userType === SUBCONTRACTOR_TYPE.FUNDING_SOURCE
  ) {
    return permissions.filter(permission => !RESTRICTED_PERMISSIONS_NORMAL_SUBCONTRACTOR_FUNDING.includes(permission));
  }
  
  // Default: return all permissions (should not reach here)
  return permissions;
};

const normalizeSessionUser = (session: SessionBootstrapResponse, tenantId?: string): User => {
  const rawPermissions = Array.isArray(session?.permissions) ? session.permissions : [];
  const subcontractorType = session?.subcontractor;
  
  return {
    id: String(session?.user_id ?? session?.id ?? ""),
    email: session?.email ?? session?.login ?? "",
    name: session?.user_name ?? session?.login ?? "",
    ...(session?.login ? { login: session.login } : {}),
    userType: session?.user_type ?? session?.userType ?? "",
    roles: Array.isArray(session?.roles) ? session.roles : [],
    permissions: filterPermissionsByUserType(rawPermissions, subcontractorType),
    tenantId: session?.tenant_id ?? session?.tenantId ?? tenantId ?? "default",
    ...(subcontractorType !== undefined ? { subcontractor: subcontractorType } : {}),
    companyIds: session?.company_ids ?? [],
    fundingSourceIds: session?.funding_source_ids ?? [],
    centerId: session?.center_id ?? null,
    defaultRunPoolId: session?.default_run_pool_id ?? null,
    accessUnassignedNoRunTrips: session?.access_unassigned_no_run_trips ?? false,
    authMode: session?.auth_mode === 'ttauth' ? 'ttauth' : 'password',
  };
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { tenantId } = useTenantContext();
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    loading: true,
    error: null,
  });

  const tenantIdRef = useRef(tenantId);

  // ECOPD-18285 - TTAuth sign-in state.
  const [loginMode, setLoginMode] = useState<LoginMode | null>(null);
  const [loginConfigError, setLoginConfigError] = useState<TtauthLoginError | null>(null);
  const [ttauthError, setTtauthError] = useState<TtauthLoginError | null>(null);
  // Loaded once per page: the agency's sign-in options do not change mid-session.
  const loginModePromiseRef = useRef<Promise<LoginMode | null> | null>(null);

  const ensureLoginMode = useCallback((): Promise<LoginMode | null> => {
    if (!loginModePromiseRef.current) {
      loginModePromiseRef.current = fetchLoginMode().then((result) => {
        if (!result.ok) {
          setLoginConfigError(result.error);
          // Not cached: the next call (next bootstrap / login attempt) asks again.
          loginModePromiseRef.current = null;
          return null;
        }
        setLoginConfigError(null);
        setLoginMode(result.mode);
        if (result.mode !== 'password') {
          loadTtauthSessionScript();
        }
        return result.mode;
      });
    }
    return loginModePromiseRef.current;
  }, []);

  // Create tenant-aware API client.
  //
  // MUST be memoized on tenantId: this value is exposed via context and consumed
  // by useApiClient(), whose consumers (the navbar badge-count hooks) key their
  // useEffect on the apiClient identity. Recreating the axios instance on every
  // render made every setAuthState() re-fire those effects, which — combined with
  // the auth-error → session-expired → bootstrapSession → setAuthState path — spun
  // an unbounded request loop (see ECOPD loop investigation).
  const apiClient = useMemo(
    () => (tenantId ? createTenantApiClient(tenantId) : defaultApiClient),
    [tenantId],
  );

  /** Turn a BFF SessionResponse (GET /session or the TTAuth exchange) into auth state. */
  const applySession = useCallback(async (sessionData: SessionBootstrapResponse): Promise<void> => {
    setServerTimeContext({
      serverNow: sessionData.server_now ?? null,
      serverToday: sessionData.server_today ?? null,
      serverTimezone: sessionData.server_timezone ?? null,
    });
    // The agency/tenant scope Conv-AI states back on every /query. Not a
    // trust boundary — Conv-AI re-resolves the authoritative agency from its
    // own BFF introspection and rejects a mismatch — so this only has to be
    // the session's own answer, never a guess. See agencyContextStore.
    setAgencyContext(
      sessionData.agency_id ?? null,
      sessionData.tenant_id ?? sessionData.tenantId ?? null,
    );
    let normalizedUser = normalizeSessionUser(sessionData, tenantIdRef.current);

    // For Normal (0) and Subcontractor (1) users whose session did not include
    // company_ids, fetch the user record to populate companyIds so that
    // provider-scoped dropdowns (e.g. ProviderSelect via useUserScope) correctly
    // restrict the visible provider list to the user's allowed providers.
    const userId = sessionData.user_id ?? sessionData.id;
    const subcontractor = sessionData.subcontractor;
    const sessionHasCompanyIds =
      Array.isArray(sessionData.company_ids) && sessionData.company_ids.length > 0;
    if (
      userId &&
      (subcontractor === SUBCONTRACTOR_TYPE.NORMAL || subcontractor === SUBCONTRACTOR_TYPE.COMPANY) &&
      !sessionHasCompanyIds
    ) {
      try {
        const userRes = await defaultApiClient.post(
          apiRoutes.proxy('support/internal/user/query'),
          {
            filter: [
              'id',
              ['companies', 'id'],
            ],
            query: [['equal', 'id', userId]],
            options: {
              timeout: 10,
              offset: 0,
              limit: 1,
              calculate_total: false,
              return_results: true,
              select_distinct: true,
            },
          }
        );
        // The user/query endpoint returns User objects in data.data
        const userRecord = userRes.data?.data?.[0] as { id: number; companies?: { id: number }[] } | undefined;
        if (userRecord) {
          const companies = userRecord.companies ?? [];
          const companyIds = companies.map((c) => Number(c.id)).filter((id) => !Number.isNaN(id));
          if (companyIds.length > 0) {
            normalizedUser = { ...normalizedUser, companyIds };
          }
        }
      } catch {
        // Non-fatal: fall back to unrestricted list if the supplemental fetch fails.
      }
    }

    setAuthState({
      user: normalizedUser,
      isAuthenticated: true,
      loading: false,
      error: null,
    });

  }, []);

  const bootstrapSession = useCallback(async (): Promise<boolean> => {
    const response = await fetch(apiRoutes.session(), {
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
    });

    if (response.status === 401) {
      // ECOPD-18285: no BFF session. If this agency offers TTAuth and the browser is signed
      // in to TTAuth (just back from the TTAuth sign-in page, or the BFF session expired
      // while the TTAuth one did not), turn that into a BFF session: one attempt, no
      // navigation. Anything else leaves the user on the login page.
      const mode = await ensureLoginMode();
      if (mode === 'ttauth') {
        // Shared with any parallel check: a second exchange would rotate the K3 token.
        const exchange = await signInWithTtauth();
        if (exchange.ok) {
          setTtauthError(null);
          clearTtauthRedirectMark();
          await applySession(exchange.session as SessionBootstrapResponse);
          return true;
        }
        if (exchange.reason === 'error') {
          setTtauthError(exchange.error);
        }
      }
      clearAgencyContext();
      setAuthState({
        user: null,
        isAuthenticated: false,
        loading: false,
        error: null,
      });
      return false;
    }

    if (!response.ok) {
      throw new Error(`Session request failed with status ${response.status}`);
    }

    const sessionData: SessionBootstrapResponse = await response.json();
    await applySession(sessionData);
    return true;
  }, [applySession, ensureLoginMode]);

  // ECOPD-14897: After a self-password-change, k3 invalidates the user's auth
  // token stored inside the RoR session.  The only way to get fresh k3 credentials
  // without forcing the user through /login is to POST to the login endpoint with
  // the new password, which makes RoR create a new session (new cookie + new k3
  // token), and then call bootstrapSession() to refresh auth state.
  const relogin = useCallback(async (login: string, password: string): Promise<boolean> => {
    try {
      const response = await fetch(apiRoutes.login(), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ login, password }),
      });

      if (!response.ok) {
        return false;
      }

      // Refresh auth state with the new session
      return await bootstrapSession();
    } catch {
      return false;
    }
  }, [bootstrapSession]);

  const logout = async () => {
    // ECOPD-18285: a TTAuth session must also sign out of TTAuth (the BFF answers with
    // redirect_to), otherwise TTAuth signs the browser straight back in.
    let redirectTo = "/login";
    try {
      const response = await defaultApiClient.post<{ redirect_to?: string }>(apiRoutes.logout());
      const target = response.data?.redirect_to;
      if (typeof target === "string" && target.startsWith("/ttauth/v1/cookie/logout")) {
        redirectTo = target;
      }
    } catch (_error) {
      // Ignore logout errors and force local signed-out state.
    } finally {
      // Clear the persisted active-dashboard id so the next login always
      // opens the user's pinned dashboard (or the system default) instead
      // of blindly restoring whatever was open in the previous session.
      // Page refreshes within the same session are unaffected because this
      // code only runs on an explicit logout — the key is written back to
      // localStorage as soon as the user selects a dashboard after login.
      try {
        localStorage.removeItem("dashboard:activeDashboardId");
      } catch {
        // localStorage may be unavailable in sandboxed environments — ignore.
      }
      clearAgencyContext();
      // `loading: true`, not a signed-out state: the page is being replaced. A signed-out
      // render would route to /login, whose TTAuth hand-over navigates to TTAuth sign-in
      // and cancels this navigation - TTAuth sign-out never runs and TTAuth signs the
      // browser straight back in (every tab stays signed in).
      setAuthState({
        user: null,
        isAuthenticated: false,
        loading: true,
        error: null,
      });
      window.location.assign(redirectTo);
    }
  };

  // Check if user has specific permission
  const can = (permission: string): boolean => {
    return authState.user?.permissions?.includes(permission) || false;
  };

  // Check if user has specific role
  const hasRole = (role: string): boolean => {
    return authState.user?.roles?.includes(role) || false;
  };

  // Check if user has any of the specified roles
  const hasAnyRole = (roles: string[]): boolean => {
    return roles.some((role) => hasRole(role));
  };

  // Initialize auth state on mount
  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      setAuthState((prev) => ({ ...prev, loading: true, error: null }));

      // Initialize feature toggle service with mock data in development
      if (env.NODE_ENV === "development") {
        const featureService = FeatureToggleService.getInstance();
        featureService.initializeMockData();
      }

      try {
        // Loads the agency's sign-in options (and the TTAuth session script when TTAuth is
        // offered) even when a BFF session already exists.
        await ensureLoginMode();
        await bootstrapSession();
      } catch (error) {
        if (!isMounted) {
          return;
        }
        setAuthState({
          user: null,
          isAuthenticated: false,
          loading: false,
          error: error instanceof Error ? error.message : "Failed to load session",
        });
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
    };
  }, [bootstrapSession, ensureLoginMode]);

  // ECOPD-14897: Listen for genuine HTTP 401 responses from the axios interceptor.
  // Before hard-redirecting to /login, attempt bootstrapSession() to confirm the
  // session is truly gone.  Self-password-change scenarios are handled separately
  // via relogin() in useUpdateUser — not here.
  useEffect(() => {
    let handling = false;
    let lastConfirmedAt = 0;
    // A proxied K3 call can return error_class:"authentication_error" for reasons
    // unrelated to the BFF session actually being gone (e.g. a specific query the
    // backend rejects). In that case bootstrapSession() keeps succeeding, so
    // re-confirming on every such event just generates load. Once we've confirmed
    // the session is still valid, ignore further session-expired events for a
    // cooldown window; a genuine expiry still redirects on the first event.
    const CONFIRM_COOLDOWN_MS = 30_000;

    const handleSessionExpired = async () => {
      // Debounce: if multiple events fire in quick succession only handle once.
      if (handling) return;
      // Circuit breaker: skip if we recently confirmed the session is healthy.
      if (Date.now() - lastConfirmedAt < CONFIRM_COOLDOWN_MS) return;
      handling = true;
      try {
        const ok = await bootstrapSession();
        if (ok) {
          lastConfirmedAt = Date.now();
        } else {
          // RoR session is gone — redirect to login.
          window.location.assign("/login");
        }
      } catch {
        window.location.assign("/login");
      } finally {
        handling = false;
      }
    };

    window.addEventListener("auth:session-expired", handleSessionExpired);
    return () => {
      window.removeEventListener("auth:session-expired", handleSessionExpired);
    };
  }, [bootstrapSession]);

  const contextValue = useMemo<AuthContextType>(() => ({
    ...authState,
    bootstrapSession,
    relogin,
    logout,
    loginMode,
    loginConfigError,
    ttauthError,
    can,
    hasRole,
    hasAnyRole,
    apiClient,
    permissions: authState.user?.permissions || [],
  }), [authState, bootstrapSession, relogin, logout, loginMode, loginConfigError, ttauthError, can, hasRole, hasAnyRole, apiClient]);

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
